import {
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  ReactNode,
  useCallback,
} from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'

import { mtc } from '../../../utils/style-helpers'
import {
  contentStyles,
  itemStyles,
  itemTextStyles,
  scrollButtonStyles,
  selectValueStyles,
  triggerStyles,
  viewportStyles,
} from './styles'
import { SelectProps as RadixSelectProps } from '@radix-ui/react-select'
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Icon } from '../icon'
import { Button } from '../button'

const SelectItem = forwardRef<
  ElementRef<typeof SelectPrimitive.Item>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    variant?: 'default' | 'text'
  }
>(({ className, children, variant = 'default', ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={mtc(itemStyles(), className)}
    {...props}
  >
    <span className="select-check absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <Icon icon={solid('check')} className="h-4 w-4" />
    </span>
    <SelectPrimitive.ItemText
      className={mtc(itemTextStyles({ variant }), className)}
    >
      {children}
    </SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = 'SelectItem'

const SelectScrollUpButton = forwardRef<
  ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={mtc(scrollButtonStyles(), className)}
    {...props}
  >
    <Icon
      icon={regular('chevron-up')}
      className="h-4 w-4"
      color="text-neutral-500"
    />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = 'SelectScrollUpButton'

const SelectScrollDownButton = forwardRef<
  ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={mtc(scrollButtonStyles(), className)}
    {...props}
  >
    <Icon
      icon={regular('chevron-down')}
      className="h-4 w-4"
      color="text-neutral-500"
    />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName = 'SelectScrollDownButton'

const SelectContent = forwardRef<
  ElementRef<typeof SelectPrimitive.Content>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    position?: 'item-aligned' | 'popper'
    variant?: 'default' | 'text'
  }
>(
  (
    { className, children, position = 'popper', variant = 'default', ...props },
    ref
  ) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={mtc(contentStyles({ position, variant }), className)}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={mtc(viewportStyles({ position, variant }), className)}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
)
SelectContent.displayName = 'SelectContent'

const SelectGroup = SelectPrimitive.Group
const SelectValue = SelectPrimitive.Value

const SelectTrigger = forwardRef<
  ElementRef<typeof SelectPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    variant?: 'default' | 'text'
    'data-testid'?: string
  }
>(
  (
    {
      className,
      children,
      variant = 'default',
      'data-testid': testId,
      ...props
    },
    ref
  ) => (
    <SelectPrimitive.Trigger
      ref={ref}
      className={mtc(
        triggerStyles({ variant, hasValue: Boolean(props.value) }),
        'w-full justify-between items-center',
        className
      )}
      data-testid={testId}
      {...props}
    >
      {children}
    </SelectPrimitive.Trigger>
  )
)
SelectTrigger.displayName = 'SelectTrigger'

const SelectLabel = forwardRef<
  ElementRef<typeof SelectPrimitive.Label>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={mtc(
      'px-2 py-1.5 text-xs font-semibold text-neutral-700',
      className
    )}
    {...props}
  />
))
SelectLabel.displayName = 'SelectLabel'

const SelectSeparator = forwardRef<
  ElementRef<typeof SelectPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={mtc('-mx-1 my-1 h-px bg-muted', className)}
    {...props}
  />
))
SelectSeparator.displayName = 'SelectSeparator'

// ──────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────
export type SelectValueType = boolean | number | string | undefined

export interface SelectDropdownOption<T> {
  key?: string | number
  value?: T
  text: string
  onClick?: () => void
}
export interface SelectProps<T, C extends boolean = false>
  extends Omit<RadixSelectProps, 'value' | 'onValueChange'> {
  options?: SelectDropdownOption<T>[]
  value?: T | undefined
  onValueChange?: C extends true
    ? (val: T | undefined) => void
    : (val: T) => void
  withTrigger?: boolean
  placeholder?: string
  onBlur?: () => void
  variant?: 'default' | 'text'
  children?: ReactNode
  clearable?: C
  searchable?: boolean
  position?: 'item-aligned' | 'popper'
}

export const selectValsToDropdownOptions = <T extends SelectValueType>(
  optionValues: readonly T[],
  formatLabel?: (enumValue: T) => string
) =>
  optionValues.map((val) => ({
    key: val?.toString() || 'undefined',
    text: formatLabel ? formatLabel(val) : val?.toString() || 'undefined',
    value: val?.toString() || '',
  }))

const SelectRoot = <T extends SelectValueType, C extends boolean = false>({
  children,
  options,
  withTrigger = true,
  placeholder,
  onBlur,
  value,
  variant = 'default',
  clearable,
  searchable = false,
  position = 'popper',
  onValueChange,
  ...rest
}: SelectProps<T, C>) => {
  const isManual = Boolean(children)
  // Bridge Radix string-only value ⇄ wrapper undefined
  const radixValue = value !== undefined ? String(value) : ''
  const handleRadixChange = useCallback(
    (val: string) => {
      if (clearable && val === '') {
        // @ts-expect-error this should be caught by the clearable check but it's not
        onValueChange?.(undefined)
      } else {
        onValueChange?.(val as T)
      }
    },
    [clearable, onValueChange]
  )
  return (
    <SelectPrimitive.Root
      value={radixValue}
      onValueChange={handleRadixChange}
      onOpenChange={(open) => !open && onBlur?.()}
      {...rest}
    >
      {isManual ? (
        children
      ) : (
        <>
          {withTrigger && (
            <SelectTrigger
              variant={variant}
              data-testid={`dropdown-${rest.name ?? ''}`}
            >
              <SelectValue
                placeholder={placeholder}
                className={mtc(
                  selectValueStyles({ hasValue: Boolean(value) }),
                  searchable ? 'pr-8' : undefined
                )}
              />

              {/* right-side icon: clear button or caret/search */}
              {clearable && value !== undefined ? (
                <Button
                  variant="discreet"
                  type="button"
                  tabIndex={-1}
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation()
                    // @ts-expect-error this should be caught by the clearable check but it's not
                    onValueChange?.(undefined)
                  }}
                  className="flex items-center text-neutral-500 hover:text-neutral-700 px-0"
                >
                  <Icon icon={solid('times')} className="h-4 w-4" />
                </Button>
              ) : (
                <SelectPrimitive.Icon asChild>
                  <Icon
                    icon={searchable ? solid('search') : solid('caret-down')}
                    className="h-4 w-4"
                    color="text-neutral-500"
                  />
                </SelectPrimitive.Icon>
              )}
            </SelectTrigger>
          )}

          {options && (
            <SelectContent variant={variant} position={position}>
              {options.map(({ key, value, text, onClick }) => (
                <SelectItem
                  variant={variant}
                  value={String(value)}
                  key={key ?? String(value)}
                  onClick={onClick}
                >
                  {text}
                </SelectItem>
              ))}
            </SelectContent>
          )}
        </>
      )}
    </SelectPrimitive.Root>
  )
}
SelectRoot.displayName = 'Select'

export const Select = Object.assign(SelectRoot, {
  Trigger: SelectTrigger,
  Content: SelectContent,
  Item: SelectItem,
  Icon: SelectPrimitive.Icon,
  Group: SelectGroup,
  Value: SelectValue,
  Label: SelectLabel,
  Separator: SelectSeparator,
  ScrollUpButton: SelectScrollUpButton,
  ScrollDownButton: SelectScrollDownButton,
})
